import { Injectable } from "@angular/core";
import { CreateOrganizationRequest, OrganizationModel, UpdateOrganizationRequest } from "../models/data/organization.model";
import { ApiBaseUrls, ApiControllers } from "../constants/constants";
import { OrganizationsFilter } from "../filters/organizations.filter";
import { PaginatedListModel } from "../models/service/paginated-list.model";
import { PaginationResponseHeader } from "../models/service/pagination-response-header.model";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { HttpClient } from "@angular/common/http";
import { APISanitizerUtilities } from "../utilities/api-sanitizer.utilities";
import { ServiceResultModel } from "../models/service/service-result.model";
import { AuditLog } from "../models/service/audit-log.model";
import { OrganizationAuditLogModel } from "../models/data/organization-audit-log.model";
import { AuditLogMapper } from "../utilities/audit-log-mapper.utilities";
import { StripeAccountSessionResponse, StripeConnectionResponse } from "../models/service/stripe-connect.model";
import { ApiResponseUtilities } from "../utilities/api-response.utilities";
import { ServiceUtilities } from "../utilities/service.utilities";
import { OrganizationsMessagingConstants } from "../constants/messaging.constants";
import { OrganizationSettingModel, UpdateOrganizationInternalSettingRequest, UpdateOrganizationSettingRequest } from "../models/data/organization-setting.model";
import { UpdateOrganizationContactsRequest } from "../models/data/organization-contact.model";
import { UpdateOrganizationAddressesRequest } from "../models/data/address.model";
import { OrganizationFeatureFlagsModel } from "../models/data/feature-flag.model";

@Injectable({
    providedIn: 'root'
  })
  export class OrganizationService {

  private apiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.ORGANIZATIONS()}`;

  constructor(private httpClient: HttpClient) {}

  public async getOrganizationById(organizationId: number, options?: { includeContacts?: boolean; includeAddresses?: boolean }): Promise<ServiceResultModel<OrganizationModel | undefined>> {
    try {
      const queryParams: string[] = [];
      if (options?.includeContacts) queryParams.push('includeContacts=true');
      if (options?.includeAddresses) queryParams.push('includeAddresses=true');
      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      const requestUrl = this.apiPath + `/${organizationId}${queryString}`;
      const response = await firstValueFrom(this.httpClient.get<OrganizationModel>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));
      const organization = <OrganizationModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: organization, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async searchOrganizations(organizationsFilter: OrganizationsFilter, orderBy?: string, skip?: number, take?: number): Promise<ServiceResultModel<PaginatedListModel<OrganizationModel>>> {
    try {
      orderBy = (orderBy) && encodeURIComponent(orderBy);
      const requestUrl = this.apiPath + `/filter?orderBy=${orderBy}&skip=${skip}&take=${take}`;

      const response = await firstValueFrom(
        this.httpClient.post<OrganizationModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<OrganizationsFilter>(organizationsFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<OrganizationModel> = {
        data: <OrganizationModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createOrganization(organization: CreateOrganizationRequest): Promise<ServiceResultModel<OrganizationModel | undefined>> {
    try {
      const requestUrl = this.apiPath;

      const response = await firstValueFrom(
        this.httpClient.post<OrganizationModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<CreateOrganizationRequest>(organization)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const createdOrganization = <OrganizationModel>response.body?.valueOf();;
      return <ServiceResultModel>{ data: createdOrganization, success: true, message: OrganizationsMessagingConstants.ORGANIZATIONS_ADD_ORGANIZATION_SUCCESS(), statusCode: response.status };
    } catch (error: any) {
      if (ApiResponseUtilities.isConflictStatusCode(error?.error?.statusCode)) {
        return <ServiceResultModel>{ success: false, message: OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_SLUG_IN_USE(), statusCode: error?.error?.statusCode };
      }

      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async setActivation(organizationId: number, active: boolean): Promise<ServiceResultModel<OrganizationModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/status?active=${active}`;

      const response = await firstValueFrom(
        this.httpClient.patch<OrganizationModel>(
          requestUrl,
          null,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const updatedOrg = <OrganizationModel>response.body?.valueOf();

      const messageData = {organizationName: updatedOrg.name, actionText: (active === true) ? "reactivated" : "deactivated"};
      const message = OrganizationsMessagingConstants.ORGANIZATIONS_UPDATE_STATUS_SUCCESS().replaceValuesWithObject(messageData);

      return <ServiceResultModel>{ data: updatedOrg, success: true, message: message, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getAuditLogsByOrganizationId(organizationId: number, skip: number, take: number, includeDetails: boolean = false): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      let requestUrl = this.apiPath + `/${organizationId}/logs?skip=${skip}&take=${take}`;
      if (includeDetails) {
        requestUrl += `&includeDetails=true`;
      }
      const response = await firstValueFrom(this.httpClient.get<OrganizationAuditLogModel[]>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));
      const mappedData = AuditLogMapper.mapOrganizationAuditLogs(<OrganizationAuditLogModel[]>response.body?.valueOf());
      
      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: mappedData,
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, message: "Audit logs loaded successfully." };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getStripeAccountSession(organizationId: number): Promise<ServiceResultModel<StripeAccountSessionResponse>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/stripe/account-session`;
      const response = await firstValueFrom(
        this.httpClient.post<StripeAccountSessionResponse>(
          requestUrl,
          null,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const accountSession = <StripeAccountSessionResponse>response.body?.valueOf();
      return <ServiceResultModel>{ data: accountSession, success: true, message: "Account session created successfully.", statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getStripeConnection(organizationId: number): Promise<ServiceResultModel<StripeConnectionResponse>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/stripe/connection`;
      const response = await firstValueFrom(
        this.httpClient.get<StripeConnectionResponse>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const stripeConnection = <StripeConnectionResponse>response.body?.valueOf();
      return <ServiceResultModel>{ data: stripeConnection, success: true, message: "Stripe connection retrieved successfully.", statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async deleteStripeConnection(organizationId: number): Promise<ServiceResultModel<boolean>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/stripe/connection`;
      const response = await firstValueFrom(
        this.httpClient.delete<boolean>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const deleted = <boolean>response.body?.valueOf();
      return <ServiceResultModel>{ data: deleted, success: true, message: "Stripe connection deleted successfully.", statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateOrganization(organizationId: number, organization: UpdateOrganizationRequest): Promise<ServiceResultModel<OrganizationModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}`;
      const response = await firstValueFrom(
        this.httpClient.patch<OrganizationModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateOrganizationRequest>(organization)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const updatedOrganization = <OrganizationModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedOrganization, success: true, message: "Organization updated successfully.", statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async uploadOrganizationAvatar(organizationId: number, avatar: File): Promise<ServiceResultModel<OrganizationModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/avatar`;

      const formData = new FormData();
      formData.append('avatar', avatar);

      const response = await firstValueFrom(
        this.httpClient.patch<OrganizationModel>(requestUrl, formData, { observe: "response" }));

      const organization = <OrganizationModel>response.body?.valueOf();

      const messageData = { organizationName: organization.name };
      const message = OrganizationsMessagingConstants.ORGANIZATIONS_UPLOAD_AVATAR_SUCCESS().replaceValuesWithObject(messageData);

      return <ServiceResultModel>{
        data: organization,
        success: true,
        message: message,
        statusCode: response.status
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getOrganizationSettings(organizationId: number): Promise<ServiceResultModel<OrganizationSettingModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/settings`;
      const response = await firstValueFrom(this.httpClient.get<OrganizationSettingModel>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));
      const organizationSettings = <OrganizationSettingModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: organizationSettings, success: true, message: "Organization settings retrieved successfully.", statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getOrganizationFeatureFlags(organizationId: number): Promise<ServiceResultModel<OrganizationFeatureFlagsModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/feature-flags`;
      const response = await firstValueFrom(this.httpClient.get<OrganizationFeatureFlagsModel>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));
      const featureFlags = <OrganizationFeatureFlagsModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: featureFlags, success: true, statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateOrganizationContacts(organizationId: number, request: UpdateOrganizationContactsRequest): Promise<ServiceResultModel<OrganizationModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/contacts`;
      const response = await firstValueFrom(
        this.httpClient.patch<OrganizationModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateOrganizationContactsRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );
      const updatedOrganization = <OrganizationModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedOrganization, success: true, message: OrganizationsMessagingConstants.ORGANIZATIONS_CONTACT_SAVE_SUCCESS(), statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateOrganizationAddresses(organizationId: number, request: UpdateOrganizationAddressesRequest): Promise<ServiceResultModel<OrganizationModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/addresses`;
      const response = await firstValueFrom(
        this.httpClient.patch<OrganizationModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateOrganizationAddressesRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );
      const updatedOrganization = <OrganizationModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedOrganization, success: true, message: OrganizationsMessagingConstants.ORGANIZATIONS_ADDRESS_SAVE_SUCCESS(), statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateOrganizationSettings(organizationId: number, settings: UpdateOrganizationSettingRequest): Promise<ServiceResultModel<OrganizationSettingModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/settings`;
      const response = await firstValueFrom(
        this.httpClient.patch<OrganizationSettingModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateOrganizationSettingRequest>(settings)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );
      const updatedSettings = <OrganizationSettingModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedSettings, success: true, message: "Organization settings updated successfully.", statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateOrganizationInternalSettings(organizationId: number, settings: UpdateOrganizationInternalSettingRequest): Promise<ServiceResultModel<OrganizationSettingModel>> {
    try {
      const requestUrl = this.apiPath + `/${organizationId}/internal-settings`;
      const response = await firstValueFrom(
        this.httpClient.patch<OrganizationSettingModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateOrganizationInternalSettingRequest>(settings)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );
      const updatedSettings = <OrganizationSettingModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedSettings, success: true, message: "Organization settings updated successfully.", statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}